// chat/Chat.tsx — the chat panel UI (fleet App-Brain pattern). It drives EVERYTHING the
// app can do by typing (G3/G16): start a practice (paste a job posting straight in), coach
// an answer back-and-forth, keep best answers, search history, progress, backup — the
// "use this app to its fullest from the chat" surface. All styling is namespaced under
// .rbc-* so it can't collide with the app CSS.
//
// G17 multi-action: the brain returns an ARRAY of actions; this client runs EVERY
// non-destructive one in order and holds the FIRST destructive action for a Confirm tap
// (a chat message can't destroy data on its own) — mirrors Expense/Study.

import { useState, useRef, useEffect } from "react";
import { relativeDays } from "../store";
import { askBrain, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton } from "./photo";
import { cloudBackupStatus } from "../brain";
import { executeAction, SENIORITY_LABELS, TYPE_LABELS, type ChatAction, type FilterRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

const STORE_KEY = "oriora.interview.chat.v1"; // device-only; lost on clear/reinstall

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    // ⚠️ Never promise that something "never leaves the device" — we promise only that
    // Oriora doesn't RETAIN it. The photo genuinely is read on-device and only its text
    // goes into the chat, so say exactly that and no more.
    "Tell me what to do — I'll start a practice (paste a job posting and I'll structure it), coach an answer with you until it's strong, keep your best answers, or find anything in your history. Tap 📷 to photograph a posting: it's read on your device, and only the text goes into the chat.\n\n" +
    'e.g. "prepare me for this: <paste the posting>" · "coach this answer: <question> — <your answer>" · "save that as a best answer" · "how am I doing?"',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

/** Serialize the visible conversation for "save this conversation to my cloud". */
function conversationText(turns: ChatTurn[]): string {
  return turns.map((t) => `${t.role === "user" ? "Me" : "Coach"}: ${t.content}`).join("\n\n");
}

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
const destructive = (k: ChatAction["kind"]) =>
  k === "backup_restore" || k === "import_file" || k === "delete_session" || k === "delete_best" ||
        k === "library_remove" || k === "library_merge";

const warnFor = (action: ChatAction): string =>
  
  action.kind === "library_merge"
    ? `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : action.kind === "library_remove"
      ? `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : action.kind === "import_file"
    ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
    : action.kind === "backup_restore"
      ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
      : action.kind === "delete_best"
        ? `Remove ${action.label ?? "this best answer"} from your library? Tap Confirm, or Cancel to keep it.`
        : `Delete ${(action as { label?: string }).label ?? "this session"} with its answers and coaching? This can't be undone — tap Confirm, or Cancel to keep it.`;

const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";

// A held destructive action PLUS the rest of its queue (and the conversation snapshot the
// save_to_cloud transform needs). Confirm runs the action then RESUMES the queue, re-holding
// at any further destructive; Cancel drops the tail and says so. Without the tail, "delete
// the old session and start a new practice" silently never starts — the G17 half-work class.
type PendingHold = { action: ChatAction; rest: ChatAction[]; convo: string };

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A destructive action is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  // The ROOM's live mock interview hands its transcript here (one-chat rule: every call's
  // transcript lives in this SAME thread, whichever surface started the call). Device-only:
  // the words go straight from the call overlay into this local store.
  useEffect(() => {
    const onCallTranscript = (e: Event) => {
      const d = (e as CustomEvent<{ lines?: { role: string; text: string }[]; seconds?: number }>).detail;
      const lines = Array.isArray(d?.lines) ? d.lines : [];
      if (lines.length === 0) return;
      const secs = Number(d?.seconds) || 0;
      const mm = Math.floor(secs / 60), ss = String(secs % 60).padStart(2, "0");
      setTurns((prev) => [
        ...prev,
        { role: "assistant" as const, content: `🎙 Live mock interview · ${mm}:${ss}` },
        ...lines
          .filter((l) => l.text.trim())
          .map((l) => ({
            role: l.role === "you" ? ("user" as const) : ("assistant" as const),
            content: l.text.trim(),
          })),
      ]);
    };
    window.addEventListener("oriora-interview:call-transcript", onCallTranscript);
    return () => window.removeEventListener("oriora-interview:call-transcript", onCallTranscript);
  }, []);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      // Run every non-destructive action in order — "start two practices" must do BOTH (G17).
      const r = await runQueue(actions, conversationText(next));
      if (r.hold) {
        setPending(r.hold);
        const warn = warnFor(r.hold.action) + tailNote(r.hold.rest);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warn].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        const body = [reply, ...r.lines].filter(Boolean).join("\n\n");
        setTurns([...next, { role: "assistant", content: body || "Done.", rows: r.rows }]);
      }
      if (r.changed) onChanged?.();
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  // Run actions in order until the first destructive one, which is returned as a hold with its tail.
  async function runQueue(queue: ChatAction[], convo: string) {
    const lines: string[] = [];
    let rows: FilterRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action.kind)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1), convo } as PendingHold };
      }
      // "Save this conversation" — the executor can't see the turns; fill the content here.
      const toRun = action.kind === "save_to_cloud" && action.source === "conversation"
        ? { ...action, content: convo, name: action.name ?? "conversation" }
        : action;
      const result = await executeAction(toRun);
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  async function confirmPending() {
    if (!pending || busy) return;
    const { action, rest, convo } = pending;
    setPending(null);
    setBusy(true);
    try {
      const result = await executeAction(action);
      const lines = result.message ? [result.message] : [];
      let rows = result.rows;
      let changed = !!result.changed;
      // Resume the rest of the request — a further destructive action re-holds, same rule.
      const r = await runQueue(rest, convo);
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      changed = changed || r.changed;
      if (r.hold) {
        setPending(r.hold);
        lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest));
      }
      setTurns((t) => [...t, { role: "assistant", content: lines.join("\n\n") || "Done.", rows }]);
      if (changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, {
      role: "assistant",
      content: skipped
        ? `Okay — kept it. I also skipped the ${skipped} remaining step${skipped > 1 ? "s" : ""} of that request; ask again if you still want ${skipped > 1 ? "them" : "it"}.`
        : "Okay — kept it. Tell me what else to do.",
    }]);
  }

  return (
    <div className="rbc-chat">
      <div className="rbc-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`rbc-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`rbc-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
            </div>
          </div>
        ))}
        {busy && <div className="rbc-msgrow"><div className="rbc-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="rbc-composer rbc-confirm">
          <button className="btn btn-danger btn-pill" onClick={confirmPending} disabled={busy}>{pending && String(pending.action.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn btn-pill" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <div className="rbc-composer">
          <textarea
            value={input}
            placeholder="Tell me what to do…"
            rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          {/* Photo IN, next to the mic (hard gate). The posting is OCR'd on-device; only its TEXT
              enters the conversation, so nothing is billed and no image leaves the phone. */}
          <PhotoButton
            onNotice={(msg) => setTurns((t) => [...t, { role: "assistant", content: msg }])}
            onText={(t) => setInput((v) => (v ? v + "\n\n" + t : t))}
            disabled={busy}
          />
          <MicButton onText={(t) => setInput((v) => (v ? v + " " : "") + t)} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. The gate here used to be iOS-only because no other path existed. */}
            {<button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
          <button className="btn btn-primary btn-pill" onClick={send} disabled={busy}>Send</button>
        </div>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="rbc-rows">
      {rows.map((r) => (
        <div key={r.id} className="rbc-rowitem">
          <span style={{ textTransform: "capitalize" }}>{r.role}<span className="muted"> · {SENIORITY_LABELS[r.seniority as keyof typeof SENIORITY_LABELS] ?? r.seniority} · {TYPE_LABELS[r.interview_type as keyof typeof TYPE_LABELS] ?? r.interview_type}</span></span>
          <span className="muted" style={{ fontSize: "var(--fs-xs)", whiteSpace: "nowrap" }}>
            {r.answered_count}/{r.question_count} · {relativeDays(r.created_at)}
          </span>
        </div>
      ))}
    </div>
  );
}
